import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EntityField } from 'c-components';
import type { Buyer, Campaign, Client } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { formatLocaleDate, LANG_en_gb } from 'c-lib';
import { useLanguageSwitcher } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';
import { clientReadPerms, dateReadPerms } from './constants';
import BookedBy from './BookedBy';

const labelSx = { color: 'text.secondary', fontSize: 12 };
const valueSx = { color: 'text.primary' };

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" px={1.5} py={0.8}>
        <Stack flex={1} minWidth={0}>
            <Typography variant="subtitle2" sx={labelSx}>
                {label}:
            </Typography>
            <Typography variant="body2" sx={valueSx} ml={label === 'User' ? -1 : 0}>
                {value}
            </Typography>
        </Stack>
    </Stack>
);

const CampaignMetaSection: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
    const { lang } = useLanguageSwitcher();
    const start = formatLocaleDate(campaign?.date_start, lang === LANG_en_gb ? 'en-GB' : 'en-US');
    const end = formatLocaleDate(campaign?.date_end, lang === LANG_en_gb ? 'en-GB' : 'en-US');
    const t = useCommonTranslation();

    return (
        <Box pl={2}>
            <IfHasAllPermissions permissions={dateReadPerms}>
                <Row
                    label="Duration"
                    value={
                        <span>
                            {start} – {end}
                        </span>
                    }
                />
            </IfHasAllPermissions>

            {campaign?.buyer?.id && (
                <IfHasAllPermissions permissions={[PermissionName.UniledportalBuyerRead]}>
                    <Row
                        label={t('Modules.Main.Campaigns.List.table.buyerCol')}
                        value={
                            <EntityField<Buyer>
                                id={campaign?.buyer_id}
                                entityName="Buyer"
                                field="name"
                            />
                        }
                    />
                </IfHasAllPermissions>
            )}
            <IfHasAllPermissions permissions={clientReadPerms}>
                <Row
                    label={t('Modules.Main.Campaigns.Overview.OverviewSection.brandHeader')}
                    value={
                        <EntityField<Client>
                            id={campaign?.client_id}
                            entityName="Client"
                            field="name"
                        />
                    }
                />
            </IfHasAllPermissions>

            <Row label="Reference" value={campaign?.reference} />
            <Row label="User" value={<BookedBy campaign={campaign} />} />
        </Box>
    );
};

export default CampaignMetaSection;
